import { useCallback, useEffect, useState } from "react";
import { Activity, Building2, Globe2, MapPin, Plug, Users } from "lucide-react";
import { useAppServices } from "../../hook/services";
import { cx, formatDate } from "../../utils";
import Card from "../../components/ui/Card";
import EmptyState from "../../components/ui/EmptyState";
import ListFilters from "../../components/ui/ListFilters";
import Pagination from "../../components/ui/Pagination";
import Skeleton from "../../components/ui/Skeleton";
import { useToast } from "../../components/ui/Toast";
import useListQuery, { useSearchInput } from "../../hook/useListQuery";
import {
  AppStack,
  CopyId,
  Count,
  DetailsState,
  Drawer,
  DrawerLoading,
  InfoGrid,
  InstallList,
  Pill,
  RecordLinks,
  Section,
  ago,
  useOpenParam,
} from "../../components/directory/shared";

/**
 * The agencies (CRM companies) behind installs: who they are, how many of
 * their locations use our apps, and who installed them.
 */

const DEFAULTS = { page: 1, limit: 20, appId: "", details: "", sort: "newest", q: "" };

export const companyLabel = (c) => c?.name || `Company ${c?.companyId || ""}`.trim();

const CompanyDrawer = ({ id, onClose }) => {
  const { directory } = useAppServices();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setData(null);
    setError("");
    directory.Company(id).then(({ response, error: err }) => {
      if (!alive) return;
      if (err || !response) setError(err?.message || "Could not load this company.");
      else setData(response);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const c = data?.company;
  const a = data?.activity;

  return (
    <Drawer
      onClose={onClose}
      label="Company details"
      header={
        c ? (
          <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-violet-50 text-violet-600">
              <Building2 size={20} />
            </span>
            <div className="min-w-0">
              <h2 className="truncate text-[16px] font-semibold tracking-tight text-ink-900">{companyLabel(c)}</h2>
              {c.ownerName && <p className="truncate text-[12.5px] text-ink-500">Owner: {c.ownerName}</p>}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {c.isWhitelabel && <Pill tone="violet" icon={Globe2}>White-label</Pill>}
                <DetailsState record={c} />
              </div>
            </div>
          </div>
        ) : (
          <Skeleton className="h-11 w-48" />
        )
      }
    >
      {error ? (
        <p className="m-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">{error}</p>
      ) : !data ? (
        <DrawerLoading />
      ) : (
        <>
          <InfoGrid
            rows={[
              ["Company ID", <CopyId value={c.companyId} />],
              ["Relationship #", c.relationshipNumber],
              ["Email", c.email],
              ["Owner", c.ownerName],
              ["White-label domain", c.whitelabelDomain],
              ["First seen", formatDate(c.createdAt)],
              ["Details updated", c.lastSyncedAt ? ago(c.lastSyncedAt) : null],
              ["Last attempt", !c.lastSyncedAt && c.syncError ? c.syncError : null],
            ]}
          />
          <Section title={`Activity · last ${a.days || 30} days`} icon={Activity}>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Count label="Messages" value={a.conversation.ok} />
              <Count label="Message errors" value={a.conversation.failed} tone="rose" />
              <Count label="Actions run" value={a.action.ok} />
              <Count label="Action errors" value={a.action.failed} tone="rose" />
            </div>
            {(a.conversation.warnings || 0) + (a.action.warnings || 0) > 0 && (
              <p className="mt-2 text-[12px] text-amber-700">
                {(a.conversation.warnings || 0) + (a.action.warnings || 0)} ignored — replies sent to another of the customer's numbers. Warnings, not errors.
              </p>
            )}
            <p className="mt-2 text-[12px] text-ink-400">Last activity: {a.lastAt ? ago(a.lastAt) : "never"}</p>
          </Section>
          <Section title={`Installs · ${data.installs.length}`} icon={Plug}>
            <InstallList installs={data.installs} />
          </Section>
          <Section title={`Locations · ${data.locations.length}`} icon={MapPin}>
            <RecordLinks
              items={data.locations}
              to={(l) => `/locations?open=${l._id}`}
              empty="No locations saved for this company yet."
              render={(l) => (
                <>
                  <span className="min-w-0 flex-1 truncate font-medium text-ink-800">{l.name || l.locationId}</span>
                  <span className="shrink-0 text-[11.5px] text-ink-400">{[l.city, l.country].filter(Boolean).join(", ")}</span>
                </>
              )}
            />
          </Section>
          <Section title={`Users · ${data.users.length}`} icon={Users}>
            <RecordLinks
              items={data.users}
              to={(u) => `/users?open=${u._id}`}
              empty="No users saved for this company yet."
              render={(u) => (
                <>
                  <span className="min-w-0 flex-1 truncate font-medium text-ink-800">{u.name || u.userId}</span>
                  <span className="shrink-0 truncate text-[11.5px] text-ink-400">{u.email}</span>
                </>
              )}
            />
          </Section>
        </>
      )}
    </Drawer>
  );
};

const Companies = () => {
  const { directory, installs } = useAppServices();
  const toast = useToast();

  const [query, setQuery] = useListQuery(DEFAULTS);
  const [search, setSearch] = useSearchInput(query.q, (q) => setQuery({ q }));
  const [openId, setOpenId] = useOpenParam();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, limit: DEFAULTS.limit });
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: query.page, limit: query.limit, sort: query.sort });
    for (const key of ["appId", "details", "q"]) if (query[key]) params.set(key, query[key]);
    const { response, error } = await directory.Companies(params.toString());
    if (error) toast(error?.message || error?.error || "Could not load companies", "error");
    const next = response && Array.isArray(response.items) ? response : { items: [], total: 0, page: 1, pages: 1, limit: query.limit };
    if (next.items.length === 0 && next.total > 0 && query.page > next.pages) setQuery({ page: next.pages });
    setData(next);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.page, query.limit, query.appId, query.details, query.sort, query.q]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    installs.Stats().then(({ response }) => setApps(response?.byApp || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = Boolean(query.q || query.appId || query.details);
  const close = useCallback(() => setOpenId(null), [setOpenId]);

  return (
    <div className="space-y-5">
      <Card padded={false}>
        <ListFilters
          search={search}
          onSearch={setSearch}
          placeholder="Search name, email, owner or ID"
          filters={[
            { label: "App", value: query.appId, onChange: (appId) => setQuery({ appId }), options: [["", "All apps"], ...apps.map((a) => [a.appId, a.appName])], className: "w-48" },
            { label: "Details", value: query.details, onChange: (details) => setQuery({ details }), options: [["", "Any details"], ["synced", "Details fetched"], ["pending", "Details pending"]], className: "w-40" },
            { label: "Sort", value: query.sort === "newest" ? "" : query.sort, onChange: (sort) => setQuery({ sort: sort || "newest" }), options: [["", "Newest first"], ["name", "Name"]], className: "w-36" },
          ]}
          onClear={() => setQuery({ q: "", appId: "", details: "" })}
        />

        {loading ? (
          <div className="divide-y divide-ink-150">
            {[0, 1, 2, 3].map((k) => (
              <div key={k} className="flex items-center gap-3 px-5 py-4">
                <Skeleton className="h-9 w-9" rounded="rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-1/4" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : data.items.length === 0 ? (
          <EmptyState
            icon={<Building2 size={24} />}
            title={filtered ? "No companies match" : "No companies yet"}
            description={filtered ? "Try another app, filter or search term." : "Companies appear here as agencies install your apps."}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[52rem] text-sm">
              <thead>
                <tr className="border-b border-ink-150 text-left">
                  {["Company", "Owner", "Locations", "Users", "Apps", "First seen"].map((h) => (
                    <th key={h} className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-150">
                {data.items.map((c) => (
                  <tr key={c._id} onClick={() => setOpenId(c._id)} className={cx("cursor-pointer transition-colors hover:bg-ink-50", openId === c._id && "bg-brand-50/50")}>
                    <td className="max-w-[18rem] px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-violet-50 text-violet-600">
                          <Building2 size={15} />
                        </span>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <button type="button" onClick={() => setOpenId(c._id)} className="truncate text-left font-medium text-ink-900 hover:text-brand-600">
                              {companyLabel(c)}
                            </button>
                            {c.isWhitelabel && <Pill tone="violet" icon={Globe2}>WL</Pill>}
                          </div>
                          {c.name ? <CopyId value={c.companyId} /> : <DetailsState record={c} />}
                        </div>
                      </div>
                    </td>
                    <td className="max-w-[14rem] px-5 py-3">
                      <p className="truncate text-[13px] text-ink-700">{c.ownerName || <span className="text-ink-300">—</span>}</p>
                      {c.email && <p className="truncate text-[11.5px] text-ink-400">{c.email}</p>}
                    </td>
                    <td className="px-5 py-3 tabular-nums text-ink-700">{c.locationCount}</td>
                    <td className="px-5 py-3 tabular-nums text-ink-700">{c.userCount}</td>
                    <td className="px-5 py-3">
                      <AppStack apps={c.apps} />
                    </td>
                    <td className="whitespace-nowrap px-5 py-3 text-[12.5px] text-ink-500">{formatDate(c.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && (
          <Pagination
            page={data.page}
            pages={data.pages}
            total={data.total}
            limit={data.limit}
            noun={data.total === 1 ? "company" : "companies"}
            onPage={(page) => setQuery({ page })}
            onLimit={(limit) => setQuery({ limit })}
          />
        )}
      </Card>

      {openId && <CompanyDrawer id={openId} onClose={close} />}
    </div>
  );
};

export default Companies;
